import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  IRelationProductGroupQuery,
  TariffSelectionAttributeValue,
} from '../tariff/product-group.interfaces';
import {
  IOperatorMapping,
  OperatorMappingId,
} from '../cms/operator-mapping.interface';
import {
  IJourneyLeg,
  IJourneyTrip,
} from '../journey-planning/journey-planning-types.interfaces';
import {
  IOnDemandBookingConfirmationRequest,
  IOnDemandBookingResponse,
  IOnDemandConnectionOption,
} from './on-demand-booking.interfaces';
import { PaymentMethod } from '../payment-methods.interfaces';
import { EConfigurationChannel } from '../cms/configuration/configuration.enums';

export interface IPassengerSummary {
  salutation: string;
  firstName: string;
  lastName: string;
  birthDate: string;
}

export interface IOnDemandPassengerDataForm {
  ticketForMe: FormControl<boolean>;
  salutation: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  birthDate: FormControl<Date | undefined>;
}

export interface IOnDemandPaymentForm {
  salutation: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  birthDate: FormControl<Date | undefined>;
  email: FormControl<string>;
  phone: FormControl<string>;
  agreement: FormControl<boolean>;
}

export interface IOnDemandPaymentCustomerData {
  salutation: string;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  email: string;
  phone: string;
  agreement: boolean;
}

export interface IOnDemandCheckoutRequest {
  bookingReference: string;
  orderReference?: string;
  totalAmount: number;
  sessionTimeout: number;
  paymentMethodId?: string;
}

export interface IOnDemandOrderConfirmationPassenger {
  name: string;
  passengerType: string;
  existingPublicTransportTicket?: string;
  extras: string[];
}

export interface IOnDemandOrderConfirmationItem {
  type: EPriceType;
  product: string;
  passenger: string;
  origin: string;
  destination: string;
  vatRate: number;
  grossPrice: number;
}

export interface IOnDemandOrderConfirmationLabels {
  passenger: string;
  operator: string;
  departure: string;
  arrival: string;
  changeableUntil: string;
  cancellableUntil: string;
  notCancellable: string;
  existingPublicTransportTickets: string;
  extras: string;
}

export interface IOnDemandOrderData {
  bookingReference: string;
  systemRef: string;
  operatorUrl: string;
  customerReference: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  totalAmount: number;
  technicalCancellationReason: string;
  paymentMethod?: PaymentMethod;
  journey: {
    date?: string;
    origin: string;
    destination: string;
    departure?: string;
    arrival?: string;
    operator?: string;
    changeableUntil?: string;
    cancellableUntil?: string;
  };
  passengers: IOnDemandOrderConfirmationPassenger[];
  publicTransportTickets: string[];
  items: IOnDemandOrderConfirmationItem[];
  labels: IOnDemandOrderConfirmationLabels;
}

export interface IOnDemandOrderConfirmationRequest extends IOnDemandOrderData {
  transactionId: string;
}

export interface IOnDemandCheckoutProcessRequest {
  order: IOnDemandOrderData;
  booking: IOnDemandBookingConfirmationRequest;
  payment: IOnDemandCheckoutRequest;
}

export interface IOnDemandCheckoutProcessResponse {
  orderId: string;
  booking: IOnDemandBookingResponse;
  redirectUrl?: string;
  completed: boolean;
}

export interface IOnDemandCheckoutCompletionResponse {
  completed: boolean;
}

export interface IOnDemandCheckoutAbortResponse {
  cancelled: boolean;
}

export interface IOnDemandOrderCancellationMailData {
  orderNumber: string;
  reason: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface IOnDemandPaymentTransaction {
  status: string;
  type: string;
  currency: string;
  value: number;
  orderReference?: string;
  purpose: string;
}

export interface IOnDemandExtraForm {
  publicTransportXml: FormControl<string>;
  name: FormControl<string>;
}

export interface IOnDemandPassengerOptionsForm {
  fahrgasttyp: FormControl<string>;
  selectionAttributes: FormArray<FormControl<TariffSelectionAttributeValue>>;
  useExistingTicket: FormControl<boolean>;
  existingTicketType: FormControl<string | null>;
  selectedExtras: FormArray<FormGroup<IOnDemandExtraForm>>;
}

export interface IOnDemandRelationProductGroupQuery extends IRelationProductGroupQuery {
  viaDescription?: string;
}

interface IOrderConfirmationPassengerSelection {
  fahrgasttyp: string;
  useExistingTicket: boolean;
  existingTicketType: string | null;
}

export interface IBuildOnDemandOrderConfirmationRequestData {
  transactionId: string;
  customerReference: string;
  customer: IOnDemandPaymentCustomerData | null;
  reservation: IOnDemandBookingResponse | null;
  booking: IOnDemandBookingResponse | null;
  trip: IJourneyTrip | null;
  connection: IOnDemandConnectionOption | null;
  operatorMapping?: IOperatorMapping | null;
  onDemandLeg?: IJourneyLeg;
  selections: IOrderConfirmationPassengerSelection[];
  passengerData: IPassengerSummary[];
  extrasPerPassenger: string[][];
  totalAmount: number;
  paymentMethod?: PaymentMethod;
  onDemandProduct: string;
  comfortProduct: string;
  technicalCancellationReason: string;
  labels: IOnDemandOrderConfirmationLabels;
  getPrice: (type: EPriceType, passengerType: string) => number;
  getPublicTransportTicketLabel: (passengerType: string) => string;
}

export interface IStoredOnDemandOrderData extends IOnDemandOrderData {
  operatorMappingId?: OperatorMappingId;
  appCode?: string;
  checkoutExpiresAt: string;
  clientChannel: EConfigurationChannel;
  guestCheckout: boolean;
  operatorCancellationCompleted?: boolean;
  paymentCompensated?: boolean;
  cancellationEmailSent?: boolean;
  cancellationReason?: string;
}

export type IBuildOnDemandOrderData = Omit<
  IBuildOnDemandOrderConfirmationRequestData,
  'transactionId'
>;

export enum EPriceType {
  PUBLIC_TRANSPORT = 'publicTransport',
  ON_DEMAND = 'onDemand',
  COMFORT = 'comfort',
}

export enum EFahrgasttyp {
  ERWACHSENER = 'Erwachsener',
  FAMILIE = 'Familie',
  KIND = 'Kind',
  SENIOR = 'Senior',
  SPEZIAL = 'Spezial',
}
